import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import {
  useLanguage,
  useLocalizedContent,
} from "../context/LanguageContext";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const { navigation } = useLocalizedContent();

  const navVariants = {
    hidden: { y: -24, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const linkVariants = {
    hidden: { opacity: 0, y: -6 },
    show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  const menuVariants = {
    hidden: { opacity: 0, y: -10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { staggerChildren: 0.08, delayChildren: 0.08 },
    },
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  const renderLanguageToggle = (isMobile = false) => {
    const group = (
      <div
        role="group"
        aria-label={navigation.languageSwitcherAria}
        className={`flex items-center rounded-full border border-white/15 bg-white/5 text-xs font-semibold uppercase tracking-[0.3em] ${
          isMobile ? "w-full justify-center py-2" : "px-2 py-1"
        }`}
      >
        {navigation.languageOptions.map((option, idx) => (
          <React.Fragment key={option.code}>
            <button
              type="button"
              onClick={() => setLanguage(option.code)}
              className={`px-2 transition-colors ${
                language === option.code
                  ? "text-white"
                  : "text-white/40 hover:text-white/80"
              }`}
              aria-pressed={language === option.code}
            >
              {option.shortLabel || option.code.toUpperCase()}
            </button>
            {idx < navigation.languageOptions.length - 1 && (
              <span className="text-white/30 text-xs px-1">|</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );

    if (isMobile) {
      return (
        <div className="pt-4 border-t border-white/10">
          <p className="text-white/50 text-xs uppercase tracking-widest mb-2">
            {navigation.languageLabel}
          </p>
          {group}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <span className="text-white/60 text-[11px] uppercase tracking-[0.3em]">
          {navigation.languageLabel}
        </span>
        {group}
      </div>
    );
  };

  return (
    <>
      <motion.nav
        variants={navVariants}
        initial="hidden"
        animate="show"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-black/60 backdrop-blur-xl border-b border-white/10 shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.a
              href="#home"
              className="flex items-center gap-2 text-white hover:opacity-90 transition-opacity"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <img
                src="/cantures-logo.png"
                alt="Cantures Logo"
                className="h-10 w-auto object-contain"
              />
            </motion.a>

            {/* Desktop Navigation */}
            <motion.div
              className="hidden md:flex items-center gap-6"
              variants={menuVariants}
              initial="hidden"
              animate="show"
            >
              {navigation.links.map((link) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  variants={linkVariants}
                  whileHover={{ y: -2, color: "#ffffff" }}
                  className="text-white/70 hover:text-white transition-colors text-sm font-medium relative group"
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-500 group-hover:w-full transition-all duration-300"></span>
                </motion.a>
              ))}
              {renderLanguageToggle()}
            </motion.div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-colors"
              whileTap={{ scale: 0.96 }}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-xl"
              onClick={() => setIsMobileMenuOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            ></motion.div>
            <motion.div
              className="fixed top-20 left-0 right-0 bg-black/90 backdrop-blur-xl border-b border-white/10"
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              transition={{ type: "spring", stiffness: 160, damping: 18 }}
            >
              <div className="container mx-auto px-6 py-8">
                <motion.div
                  className="flex flex-col gap-4"
                  variants={menuVariants}
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                >
                  {navigation.links.map((link) => (
                    <motion.a
                      key={link.name}
                      href={link.href}
                      onClick={(e) => scrollToSection(e, link.href)}
                      variants={linkVariants}
                      className="text-white/70 hover:text-white transition-colors text-lg font-medium py-2"
                    >
                      {link.name}
                    </motion.a>
                  ))}
                  {renderLanguageToggle(true)}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
